import React, { useState, useEffect } from "react";
import { TextInput, Select, Tooltip, Loader } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useGlobal } from "../../context/global-context";
import { useNavigate, useLocation } from "react-router-dom";
import VerificationIframe from "../../components/verification-iframe";
import WineBottle from "../../components/wine-bottle";
import { resolveProduct, money } from "../wine-store/inventory";
import "../../styles/wine-theme.css";
import "./checkout.css";

export default function CheckoutPage() {
  const { globalState, clearCart, removeFromCart, addToCart, updateCartItemQuantity, wineStoreSettings } = useGlobal();

  const decrementItem = (item) => {
    if (item.quantity > 1) {
      updateCartItemQuantity(item.name, item.quantity - 1);
    } else {
      removeFromCart(item.name);
    }
  };
  const navigate = useNavigate();
  const location = useLocation(); // eslint-disable-line no-unused-vars
  const isMobile = useMediaQuery("(max-width: 767px)");
  const minAge = wineStoreSettings?.wineAge ?? 21;
  const [verificationModalOpened, setVerificationModalOpened] = useState(false);
  const [verificationLink, setVerificationLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [ageVerified, setAgeVerified] = useState(false);
  const [ageFailed, setAgeFailed] = useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const [orderComplete, setOrderComplete] = useState(false);
  // Step 2 stays collapsed until step 1 is submitted (or we return from a redirect).
  const [ageStepOpen, setAgeStepOpen] = useState(
    () => !!new URLSearchParams(window.location.search).get("request_id")
  );
  const [promoCode, setPromoCode] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [formData, setFormData] = useState(() => {
    const cached = sessionStorage.getItem("checkoutFormData");
    if (cached) {
      try { return JSON.parse(cached); } catch (e) { /* ignore */ }
    }
    return {
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      dobMonth: "",
      dobDay: "",
      dobYear: "",
      ssn4: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
    };
  });
  const [formErrors, setFormErrors] = useState({});
  const [apiError, setApiError] = useState(null);

  // Handle redirect return - check for request_id in query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get("request_id");
    console.log("[Checkout] Mount check - URL:", window.location.href, "request_id:", requestId);
    if (!requestId) return;

    const checkResult = async () => {
      setLoading(true);
      setLoadingMessage("Checking verification result...");
      setApiError(null);

      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const baseUrl = isLocalhost ? "http://localhost:8080" : "https://be.demo.veratad.app";

      try {
        const response = await fetch(
          `${baseUrl}/demo/vx/get-result.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              request_id: requestId,
              demo: "wine-store",
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setApiError({
            error: errorData.error || "Error",
            message: errorData.message || "Something went wrong. Please try again.",
          });
          navigate("/checkout", { replace: true });
          return;
        }

        const data = await response.json();

        if (data.status === "PASS") {
          setAgeVerified(true);
          setAgeFailed(false);
        } else {
          setAgeFailed(true);
          setAgeVerified(false);
          setFailureCount((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Get result error:", error);
        setApiError({
          error: "Network Error",
          message: "Could not connect to the verification service. Please try again.",
        });
      } finally {
        setLoading(false);
        setLoadingMessage("");
        sessionStorage.removeItem("checkoutFormData");
        navigate("/checkout", { replace: true });
      }
    };

    checkResult();
  }, []); // Run once on mount

  const US_STATES = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
    { value: "DC", label: "District of Columbia" },
  ];

  const handleAgeVerification = async () => {
    setLoading(true);
    setLoadingMessage("Getting verification session...");
    setApiError(null);

    const isProduction = window.location.hostname === "demo.dcams.app";
    const baseUrl = isProduction ? "https://be.demo.veratad.app" : "http://localhost:8080";

    try {
      const response = await fetch(
        `${baseUrl}/demo/vx/create-journey.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email,
            ...(optionalFields.phone && formData.phone ? { phone: formData.phone.replace(/\D/g, "") } : {}),
            target: {
              ...(optionalFields.firstName && formData.firstName ? { fn: formData.firstName } : {}),
              ...(optionalFields.lastName && formData.lastName ? { ln: formData.lastName } : {}),
              ...(optionalFields.phone && formData.phone ? { phone: formData.phone.replace(/\D/g, "") } : {}),
              ...(optionalFields.dob && formData.dobYear ? { dob: `${formData.dobYear}${formData.dobMonth.padStart(2, "0")}${formData.dobDay.padStart(2, "0")}` } : {}),
              ...(optionalFields.ssn4 && formData.ssn4 ? { ssn4: formData.ssn4 } : {}),
              ...(optionalFields.address && formData.address ? { addr: formData.address, city: formData.city, state: formData.state, zip: formData.zipCode } : {}),
            },
            demo: "wine-store",
            age: `${minAge}+`,
            method: wineStoreSettings?.integrationMethod || "iframe",
            test_mode: wineStoreSettings?.testMode ?? true,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setApiError({
          error: errorData.error || "Error",
          message: errorData.message || "Something went wrong. Please try again.",
        });
        return;
      }

      const data = await response.json();

      if (data.link) {
        if ((wineStoreSettings?.integrationMethod || "iframe") === "redirect") {
          // Cache form data so it's restored when the user returns
          sessionStorage.setItem("checkoutFormData", JSON.stringify(formData));
          window.open(data.link, "_blank");
          return;
        }

        setVerificationLink(data.link);
        setVerificationModalOpened(true);
      }
    } catch (error) {
      console.error("Verification error:", error);
      setApiError({
        error: "Network Error",
        message: "Could not connect to the verification service. Please try again.",
      });
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  // Listen for messages from the iframe or redirect tab
  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "CLOSE") {
        setVerificationModalOpened(false);
      }
      if (event.data === "PASS") {
        setAgeStepOpen(true);
        setVerificationModalOpened(false);
        setAgeVerified(true);
        setAgeFailed(false);
        window.focus();
      }
      if (event.data === "FAIL") {
        setAgeStepOpen(true);
        setVerificationModalOpened(false);
        setAgeFailed(true);
        setAgeVerified(false);
        setFailureCount((prev) => prev + 1);
        window.focus();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const formatPhone = (value) => {
    let digits = value.replace(/\D/g, "");
    // Strip leading country code "1" if they typed 11 digits
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.slice(1);
    }
    // Cap at 10 digits
    digits = digits.slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleFormChange = (field, value) => {
    const newValue = field === "phone" ? formatPhone(value) : value;
    setFormData((prev) => ({ ...prev, [field]: newValue }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const optionalFields = wineStoreSettings?.optionalFields || {};

  const isContactComplete =
    formData.email &&
    /\S+@\S+\.\S+/.test(formData.email) &&
    (!optionalFields.firstName || formData.firstName) &&
    (!optionalFields.lastName || formData.lastName) &&
    (!optionalFields.phone || formData.phone) &&
    (!optionalFields.dob || (formData.dobMonth && formData.dobDay && formData.dobYear)) &&
    (!optionalFields.ssn4 || (formData.ssn4 && formData.ssn4.length === 4)) &&
    (!optionalFields.address || (formData.address && formData.city && formData.state && formData.zipCode));

  const validateForm = () => {
    const errors = {};
    if (!formData.email) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Invalid email";
    if (optionalFields.firstName && !formData.firstName) errors.firstName = "First name is required";
    if (optionalFields.lastName && !formData.lastName) errors.lastName = "Last name is required";
    if (optionalFields.phone && !formData.phone) errors.phone = "Phone number is required";
    if (optionalFields.dob && (!formData.dobMonth || !formData.dobDay || !formData.dobYear)) errors.dob = "Date of birth is required";
    if (optionalFields.ssn4 && (!formData.ssn4 || formData.ssn4.length !== 4)) errors.ssn4 = "Last 4 of SSN is required";
    if (optionalFields.address) {
      if (!formData.address) errors.address = "Address is required";
      if (!formData.city) errors.city = "City is required";
      if (!formData.state) errors.state = "State is required";
      if (!formData.zipCode) errors.zipCode = "ZIP code is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCompleteOrder = () => {
    if (validateForm()) {
      setOrderComplete(true);
    }
  };

  const inputProps = (field, extra = {}) => ({
    value: formData[field],
    onChange: (e) => handleFormChange(field, e.target.value),
    error: formErrors[field],
    required: true,
    disabled: ageVerified,
    ...extra,
  });

  const verifyDisabled = !isContactComplete || failureCount >= 2;
  const verifyLabel = loading
    ? loadingMessage
    : failureCount >= 2
      ? "Unavailable"
      : ageFailed
        ? "Try again"
        : "Verify age";

  const ageStatus = ageVerified
    ? `Verified. You are ${minAge}+.`
    : failureCount >= 2
      ? "No more attempts available. Please contact customer service."
      : ageFailed
        ? "Verification failed. Please try again."
        : `Required before alcohol can be purchased. Takes about 30 seconds and runs once per order.`;

  const step = ageVerified ? 3 : ageStepOpen ? 2 : 1;

  const handleContinueToAge = () => {
    if (validateForm()) {
      setAgeStepOpen(true);
      // On phones the age card opens below the fold; bring it into view.
      if (isMobile) {
        setTimeout(() => document.getElementById("vt-age-step")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    }
  };

  if (globalState.cart.length === 0 && !orderComplete) {
    return (
      <div className="vt-wine vt-page">
        <div className="vt-container">
          <div className="vt-card vt-state">
            <div className="vt-state__icon">
              <svg width="26" height="26" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M1.8 2.2h2.1l1.9 8.4h7.6l1.6-6H5.2" stroke="#0C0E0E" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="7" cy="14.4" r="1.2" fill="#0C0E0E" />
                <circle cx="12.8" cy="14.4" r="1.2" fill="#0C0E0E" />
              </svg>
            </div>
            <h1>Your cart is empty</h1>
            <p>Add a bottle to the cart to continue to checkout.</p>
            <button type="button" className="vt-btn vt-btn--primary vt-btn--lg" onClick={() => (window.location.href = "/wine")}>
              Continue shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="vt-wine vt-page">
        <div className="vt-container">
          <div className="vt-card vt-card--dark vt-state vt-state--done">
            <div className="vt-state__icon vt-state__icon--dark">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2.6 4.8 5.4v6c0 4.6 3 8.1 7.2 10 4.2-1.9 7.2-5.4 7.2-10v-6L12 2.6z" stroke="#00E5C7" strokeWidth="1.6" strokeLinejoin="round" />
                <path d="m8.9 11.8 2.2 2.2 4-4.2" stroke="#00E5C7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="vt-eyebrow vt-eyebrow--accent">Order placed</span>
            <h1>Thank you. Your order is on its way.</h1>
            <p>Age was verified once for this order. Nothing ships from this demo environment.</p>
            <button
              type="button"
              className="vt-btn vt-btn--on-dark vt-btn--lg"
              onClick={() => {
                clearCart();
                window.location.href = "/wine";
              }}
            >
              Continue shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  const itemCount = globalState.cart.reduce((t, i) => t + i.quantity, 0);
  const summaryCollapsed = isMobile && !summaryOpen;

  const summary = (
    <aside className={`vt-card vt-summary${summaryCollapsed ? " vt-summary--collapsed" : ""}`}>
      {isMobile ? (
        <button
          type="button"
          className="vt-summary__toggle"
          aria-expanded={summaryOpen}
          onClick={() => setSummaryOpen((o) => !o)}
        >
          <span className="vt-summary__toggle-label">
            <h2>Order summary</h2>
            <span className="vt-mono">{itemCount} {itemCount === 1 ? "item" : "items"}</span>
          </span>
          <span className="vt-summary__toggle-total">
            <span className="vt-price">{money(globalState.cartTotal)}</span>
            <svg className="vt-summary__chevron" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
        </button>
      ) : (
        <div className="vt-summary__head">
          <h2>Order summary</h2>
          <span className="vt-mono">{globalState.cart.length} {globalState.cart.length === 1 ? "line" : "lines"}</span>
        </div>
      )}

      {!summaryCollapsed && globalState.cart.map((raw) => {
        const item = resolveProduct(raw);
        return (
          <div key={item.name} className="vt-line">
            <div className="vt-thumb vt-line__thumb" style={{ background: item.tint }}>
              <WineBottle fill={item.fill} width={24} />
            </div>
            <div className="vt-line__body">
              <div className="vt-line__row">
                <span className="vt-line__name">{item.name}</span>
                <span className="vt-price">{money(parseFloat(item.cost) * item.quantity)}</span>
              </div>
              <div className="vt-line__row">
                <div className="vt-stepper">
                  <button type="button" aria-label="Decrease quantity" onClick={() => decrementItem(item)}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3.5 7h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                  </button>
                  <span>{item.quantity}</span>
                  <button type="button" aria-label="Increase quantity" onClick={() => addToCart(item)}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3.5 7h7M7 3.5v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                  </button>
                </div>
                <button type="button" className="vt-remove" aria-label={`Remove ${item.name}`} onClick={() => removeFromCart(item.name)}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M2.8 4.3h10.4M6.4 4.3V3.1h3.2v1.2M4.4 4.3l.6 8.2c0 .7.5 1.2 1.2 1.2h3.6c.7 0 1.2-.5 1.2-1.2l.6-8.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {!summaryCollapsed && (
      <>
      <div className="vt-divider" />

      <div className="vt-discount">
        <label htmlFor="vt-discount">Discount code</label>
        <div className="vt-discount__row">
          <input
            id="vt-discount"
            type="text"
            placeholder="Enter code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
          />
          <button type="button" className="vt-btn vt-btn--ghost">Apply</button>
        </div>
      </div>

      <div className="vt-divider" />

      <div className="vt-totals">
        <div className="vt-totals__row"><span>Subtotal</span><span className="vt-price">{money(globalState.cartTotal)}</span></div>
        <div className="vt-totals__row"><span>Shipping</span><span className="vt-price">Free</span></div>
        <div className="vt-totals__row vt-totals__row--grand"><span>Total</span><span className="vt-price">{money(globalState.cartTotal)}</span></div>
      </div>
      </>
      )}

      {!isMobile && (
        <>
          <button
            type="button"
            className="vt-btn vt-btn--primary vt-btn--lg vt-btn--block"
            disabled={!ageVerified}
            onClick={handleCompleteOrder}
          >
            {ageVerified ? "Complete order" : "Verify age to continue"}
          </button>
          <span className="vt-summary__note">You must be {minAge} or older. Payment is collected after verification.</span>
        </>
      )}
    </aside>
  );

  return (
    <div className="vt-wine vt-page vt-checkout">
      <div className="vt-container vt-checkout__body">
        {/* Title row */}
        <div className="vt-checkout__head">
          {!isMobile && (
            <div className="vt-checkout__title">
              <button type="button" className="vt-back" onClick={() => (window.location.href = "/wine")}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13 8H3.6M7 4.6 3.6 8 7 11.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                <span>Back to store</span>
              </button>
              <h1>Checkout</h1>
            </div>
          )}
          <ol className="vt-steps" aria-label="Checkout progress">
            {[
              [1, "Details"],
              [2, isMobile ? "Age" : "Age check"],
              [3, "Pay"],
            ].map(([n, label], i) => (
              <React.Fragment key={n}>
                {i > 0 && <li className="vt-steps__rule" aria-hidden="true" />}
                <li className={`vt-steps__item${n <= step ? " vt-steps__item--active" : ""}`}>
                  <span className="vt-steps__num">{n < step ? <Tick /> : n}</span>
                  <span>{label}</span>
                </li>
              </React.Fragment>
            ))}
          </ol>
        </div>

        <div className="vt-checkout__grid">
          {isMobile && summary}

          <div className="vt-checkout__main">
            {/* Contact */}
            <section className="vt-card vt-section">
              <div className="vt-section__head">
                <span className="vt-eyebrow">Step 1</span>
                <h2>Contact information</h2>
              </div>

              <TextInput label="Email address" placeholder="your@email.com" {...inputProps("email")} />

              {(optionalFields.firstName || optionalFields.lastName) && (
                <div className="vt-fields-2">
                  {optionalFields.firstName && <TextInput label="First name" placeholder="John" {...inputProps("firstName")} />}
                  {optionalFields.lastName && <TextInput label="Last name" placeholder="Doe" {...inputProps("lastName")} />}
                </div>
              )}
              {optionalFields.phone && <TextInput label="Phone" placeholder="(555) 123-4567" {...inputProps("phone")} />}
              {optionalFields.dob && (
                <div className="vt-fields-3">
                  <Select
                    label="Month"
                    placeholder="Month"
                    data={[
                      { value: "1", label: "January" },
                      { value: "2", label: "February" },
                      { value: "3", label: "March" },
                      { value: "4", label: "April" },
                      { value: "5", label: "May" },
                      { value: "6", label: "June" },
                      { value: "7", label: "July" },
                      { value: "8", label: "August" },
                      { value: "9", label: "September" },
                      { value: "10", label: "October" },
                      { value: "11", label: "November" },
                      { value: "12", label: "December" },
                    ]}
                    value={formData.dobMonth}
                    onChange={(value) => handleFormChange("dobMonth", value)}
                    error={formErrors.dob}
                    required
                    disabled={ageVerified}
                  />
                  <Select
                    label="Day"
                    placeholder="Day"
                    data={Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
                    value={formData.dobDay}
                    onChange={(value) => handleFormChange("dobDay", value)}
                    error={formErrors.dob ? " " : ""}
                    required
                    disabled={ageVerified}
                  />
                  <Select
                    label="Year"
                    placeholder="Year"
                    data={Array.from({ length: 100 }, (_, i) => {
                      const year = new Date().getFullYear() - 18 - i;
                      return { value: String(year), label: String(year) };
                    })}
                    value={formData.dobYear}
                    onChange={(value) => handleFormChange("dobYear", value)}
                    error={formErrors.dob ? " " : ""}
                    required
                    searchable
                    disabled={ageVerified}
                  />
                </div>
              )}
              {optionalFields.ssn4 && (
                <TextInput
                  label="Last 4 of SSN"
                  placeholder="1234"
                  maxLength={4}
                  {...inputProps("ssn4", {
                    onChange: (e) => handleFormChange("ssn4", e.target.value.replace(/\D/g, "").slice(0, 4)),
                  })}
                />
              )}
              {optionalFields.address && (
                <>
                  <TextInput label="Address" placeholder="123 Main St" {...inputProps("address")} />
                  <div className="vt-fields-3 vt-fields-address">
                    <TextInput label="City" placeholder="New York" {...inputProps("city")} />
                    <Select
                      label="State"
                      placeholder="Select state"
                      data={US_STATES}
                      value={formData.state}
                      onChange={(value) => handleFormChange("state", value)}
                      error={formErrors.state}
                      required
                      disabled={ageVerified}
                    />
                    <TextInput label="ZIP code" placeholder="10001" {...inputProps("zipCode")} />
                  </div>
                </>
              )}

              {!ageStepOpen && !isMobile && (
                <button
                  type="button"
                  className="vt-btn vt-btn--primary vt-btn--lg vt-btn--block"
                  onClick={handleContinueToAge}
                >
                  Continue to age check
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h9.4M9 4.6 12.6 8 9 11.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
              )}
            </section>

            {/* Age verification */}
            {ageStepOpen && (
            <section id="vt-age-step" className={`vt-card vt-card--dark vt-section vt-age${ageVerified ? " vt-age--ok" : ""}${ageFailed ? " vt-age--fail" : ""}`}>
              <div className="vt-age__head">
                <div className="vt-section__head">
                  <span className="vt-eyebrow vt-eyebrow--accent">Step 2</span>
                  <h2>Age verification</h2>
                  <p className={ageFailed && !ageVerified ? "vt-age__status vt-age__status--fail" : "vt-age__status"}>{ageStatus}</p>
                </div>
                <div className="vt-age__badge">
                  {ageVerified ? <Tick size={26} /> : ageFailed ? <Cross /> : <Shield />}
                </div>
              </div>

              {!ageVerified && !isMobile && (
                <div className="vt-age__cta">
                  <Tooltip
                    label={failureCount >= 2 ? "Maximum attempts reached" : "Complete the required fields above first"}
                    disabled={!verifyDisabled}
                  >
                    <button
                      type="button"
                      className="vt-btn vt-btn--on-dark vt-btn--lg vt-btn--block"
                      onClick={handleAgeVerification}
                      disabled={verifyDisabled || loading}
                      data-disabled={verifyDisabled}
                    >
                      {loading && <Loader size={14} type="dots" color="#04201C" />}
                      {verifyLabel}
                    </button>
                  </Tooltip>
                </div>
              )}

              {apiError && (
                <div className="vt-age__error" role="alert">
                  <strong>{apiError.error}</strong>
                  <span>{apiError.message}</span>
                </div>
              )}

              <div className="vt-age__chips">
                <span className="vt-chip">Government ID</span>
                <span className="vt-chip">Selfie match</span>
                <span className="vt-chip">Database check</span>
                <span className="vt-chip">No documents retained</span>
              </div>
            </section>
            )}
          </div>

          {!isMobile && summary}
        </div>

        {isMobile && (
          <p className="vt-summary__note">You must be {minAge} or older. Payment is collected after verification.</p>
        )}
      </div>

      {isMobile && (
        <div className="vt-bottom-bar">
          <div className="vt-bottom-bar__total">
            <span className="vt-eyebrow">Total</span>
            <span className="vt-mono">{money(globalState.cartTotal)}</span>
          </div>
          {ageVerified ? (
            <button type="button" className="vt-btn vt-btn--primary vt-btn--lg" onClick={handleCompleteOrder}>
              Complete order
            </button>
          ) : ageStepOpen ? (
            <button
              type="button"
              className="vt-btn vt-btn--on-dark vt-btn--lg"
              onClick={handleAgeVerification}
              disabled={verifyDisabled || loading}
              data-disabled={verifyDisabled}
            >
              {loading && <Loader size={14} type="dots" color="#04201C" />}
              {verifyLabel}
            </button>
          ) : (
            <button type="button" className="vt-btn vt-btn--primary vt-btn--lg" onClick={handleContinueToAge}>
              Continue to age check
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h9.4M9 4.6 12.6 8 9 11.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          )}
        </div>
      )}

      <VerificationIframe
        opened={verificationModalOpened}
        onClose={() => setVerificationModalOpened(false)}
        verificationLink={verificationLink}
        title="Age Verification"
      />
    </div>
  );
}

function Tick({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Cross() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" stroke="#FF7A7A" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Shield() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2.6 4.8 5.4v6c0 4.6 3 8.1 7.2 10 4.2-1.9 7.2-5.4 7.2-10v-6L12 2.6z" stroke="#00E5C7" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="m8.9 11.8 2.2 2.2 4-4.2" stroke="#00E5C7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
